import { useState, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import Spinner from "@components/Spinner/Spinner";
import { DataScienceContext } from "./context/DataScienceContext";
import GraphArea from "./graph/GraphArea";
import {
  fetchRainDataByDate,
  fetchTreeHealthByDate,
  fetchTSDataByDate,
} from "@modules/Trees/services/treeCrud";
import { getAllTasks } from "@modules/Tasks/services/taskCrud";
import { GREEN, YELLOW, RED, GREY, BLUE } from "@helper/colors";
import EmptyArea from "./components/EmptyArea";
import { getInterval, chunkArray, getCompatibleInstallations } from "./utils/dataProcessing";

import { buildPlottedSeriesColorMap } from "./utils/colorAssignment";
const CHUNK_THRESHOLD = 5;
const CHUNK_SIZE = 5;
import {
  MOISTURE_CONTENT_FIELDS,
  scaleMoistureContentForDisplay,
  scaleMoistureThresholdForDisplay,
  getMoistureContentDisplayMode,
  getMoistureContentCapEnabled,
} from "@helper/moistureContentDisplay";
import { filterInstallationsForVariable } from "./utils/variableMetadata";

function GraphContainer({ loadData, setLoadData, setShouldRefetch, shouldRefetch }) {
  const { t } = useTranslation();
  const authUser = useSelector((state) => state.auth.user);
  const moistureContentDisplayMode = getMoistureContentDisplayMode(authUser);
  const moistureContentCapEnabled = getMoistureContentCapEnabled(authUser);
  const [rainData, setRainData] = useState([]);
  const [irrigationData, setIrrigationData] = useState([]);
  const [hasLoadedDataOnce, setHasLoadedDataOnce] = useState(false);
  const {
    pid,
    filterState,
    setLYaxis,
    setRYaxis,
    setLYaxisMean,
    setRYaxisMean,
    startDate,
    endDate,
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    positionLeft,
    positionRight,
    setHealthData,
    lineColor,
    activeMean,
    activeHealthData,
    activeIrrigationData,
    activeRain,
    customInterval,
    setSeries,
    zoomTriggeredDateChange,
    setZoomTriggeredDateChange,
    setActiveSeriesColorMap,
  } = useContext(DataScienceContext);

  const {
    installationsId,
    areasId,
    installations,
    variablesId,
    treesId,
    rainAreaId,
    healthTreeId,
    irrigationTreeId,
  } = filterState;

  const tid = treesId?.length ? treesId.join(",") : null;
  const id = areasId?.length ? areasId.join(",") : null;

  useEffect(() => {
    if (loadData) setHasLoadedDataOnce(true);
  }, [loadData]);

  // Determine when data should be refetched
  useEffect(() => {
    const hasVariables = variablesId.length > 0;
    const hasRainOrHealthOrIrrigation = activeRain || activeHealthData || activeIrrigationData;
    const shouldRefetchNow = hasVariables || hasRainOrHealthOrIrrigation;
    setShouldRefetch(shouldRefetchNow);
  }, [
    variablesId,
    installationsId,
    treesId,
    areasId,
    startDate,
    endDate,
    rainAreaId,
    healthTreeId,
    irrigationTreeId,
    activeRain,
    activeHealthData,
    activeIrrigationData,
    setShouldRefetch,
  ]);
  const filterInstallations = useMemo(
    () => installations.filter((installation) => installationsId.includes(installation.id)),
    [installations, installationsId]
  );

  const leftInstallations = useMemo(
    () => filterInstallationsForVariable(filterInstallations, dragItemLeftId),
    [filterInstallations, dragItemLeftId]
  );

  const rightInstallations = useMemo(
    () => filterInstallationsForVariable(filterInstallations, dragItemRightId),
    [filterInstallations, dragItemRightId]
  );

  const plottedInstallations = useMemo(() => {
    const ids = new Set([
      ...leftInstallations.map((i) => i.id),
      ...rightInstallations.map((i) => i.id),
    ]);
    return filterInstallations.filter((i) => ids.has(i.id));
  }, [filterInstallations, leftInstallations, rightInstallations]);

  const plottedColorMap = useMemo(
    () => buildPlottedSeriesColorMap(plottedInstallations, lineColor),
    [plottedInstallations, lineColor]
  );

  // Sync resolved colors to context so sidebar checkboxes match the graph
  useEffect(() => {
    if (typeof setActiveSeriesColorMap === "function") {
      setActiveSeriesColorMap(plottedColorMap);
    }
  }, [plottedColorMap, setActiveSeriesColorMap]);

  const {
    isLoading: isRainLoading,
    isFetching: isRainFetching,
    isSuccess: isSuccessRain,
    refetch: refetchRain,
  } = _getRain({
    pid,
    id: rainAreaId || id,
    startDate,
    endDate,
    activeRain,
    setRainData,
    areasId,
  });

  const {
    isLoading: isHealthDataLoading,
    isFetching: isHealthDataFetching,
    isSuccess: isSuccessHealth,
    refetch: refetchHealth,
  } = _getHealthData({
    pid,
    healthTreeId,
    setHealthData,
    startDate,
    endDate,
    activeHealthData,
  });

  const {
    isLoading: isIrrigationDataLoading,
    isFetching: isIrrigationDataFetching,
    isSuccess: isSuccessIrrigation,
    refetch: refetchIrrigation,
  } = _getIrrigationData({
    pid,
    irrigationTreeId,
    setIrrigationData,
    startDate,
    endDate,
    activeIrrigationData,
  });

  // Clear data when deactivated
  useEffect(() => {
    if (!activeRain) setRainData([]);
  }, [activeRain]);

  useEffect(() => {
    if (!activeHealthData) setHealthData([]);
  }, [activeHealthData, setHealthData]);

  useEffect(() => {
    if (!activeIrrigationData) setIrrigationData([]);
  }, [activeIrrigationData]);

  // Date or interval changes should stage a fresh reload:
  // clear currently displayed data and wait for explicit "Load Data".
  useEffect(() => {
    if (zoomTriggeredDateChange) {
      setZoomTriggeredDateChange(false);
    }
    // Always require explicit user action after a range change (including zoom).
    setLoadData(false);
    setLYaxis([]);
    setRYaxis([]);
    setLYaxisMean([]);
    setRYaxisMean([]);
    setHealthData([]);
    setRainData([]);
    setIrrigationData([]);
    setSeries([]);
  }, [startDate, endDate, customInterval, zoomTriggeredDateChange]);

  // Refetch data when loadData is triggered
  useEffect(() => {
    if (!loadData) return;

    if (rainAreaId && activeRain) {
      refetchRain();
    }
    if (healthTreeId && activeHealthData) {
      refetchHealth();
    }
    if (irrigationTreeId && activeIrrigationData) {
      refetchIrrigation();
    }
  }, [
    loadData,
    rainAreaId,
    activeRain,
    healthTreeId,
    activeHealthData,
    irrigationTreeId,
    activeIrrigationData,
    refetchRain,
    refetchHealth,
    refetchIrrigation,
  ]);

  //fetching::begin
  let checkInitialEmpty = true;
  let checkLoading = false;
  let anyFetchSuccessfull = false;

  // Initialize data fetchers
  const {
    allResults: leftResults,
    isLoading: isLeftLoading,
    isSuccess: isLeftSuccess,
    isFetching: isLeftFetching,
    refetch: refetchLeft,
    isError: isLeftError,
    error: leftError,
  } = _getTsData({
    pid,
    installations: leftInstallations,
    setYAxis: setLYaxis,
    position: positionLeft,
    dragItem: dragItemLeft,
    startDate,
    endDate,
    lineColor: plottedColorMap,
    customInterval,
    mean: false,
    resetKey: null,
    moistureContentDisplayMode,
    moistureContentCapEnabled,
  });

  const {
    allResults: rightResults,
    isLoading: isRightLoading,
    isSuccess: isRightSuccess,
    refetch: refetchRight,
    isFetching: isRightFetching,
    isError: isRightError,
    error: rightError,
  } = _getTsData({
    pid,
    installations: rightInstallations,
    setYAxis: setRYaxis,
    dragItem: dragItemRight,
    position: positionRight,
    startDate,
    endDate,
    lineColor: plottedColorMap,
    customInterval,
    mean: false,
    resetKey: null,
    moistureContentDisplayMode,
    moistureContentCapEnabled,
  });

  const {
    allResults: leftMeanResults,
    isLoading: isLeftMeanLoading,
    isFetching: isLeftMeanFetching,
    refetch: refetchMeanLeft,
    isError: isLeftMeanError,
    error: leftMeanError,
  } = _getTsData({
    pid,
    installations: leftInstallations,
    setYAxis: setLYaxisMean,
    position: positionLeft,
    dragItem: dragItemLeft,
    startDate,
    endDate,
    lineColor: plottedColorMap,
    customInterval,
    mean: activeMean,
    resetKey: null,
    moistureContentDisplayMode,
    moistureContentCapEnabled,
  });

  const {
    allResults: rightMeanResults,
    isLoading: isRightMeanLoading,
    isFetching: isRightMeanFetching,
    refetch: refetchMeanRight,
    isError: isRightMeanError,
    error: rightMeanError,
  } = _getTsData({
    pid,
    installations: rightInstallations,
    setYAxis: setRYaxisMean,
    dragItem: dragItemRight,
    position: positionRight,
    startDate,
    endDate,
    lineColor: plottedColorMap,
    customInterval,
    mean: activeMean,
    resetKey: null,
    moistureContentDisplayMode,
    moistureContentCapEnabled,
  });

  // Refetch data when loadData is triggered
  useEffect(() => {
    if (!loadData || installationsId.length === 0) return;

    const promises = [];

    if (positionLeft && dragItemLeft) {
      promises.push(refetchLeft());
      if (activeMean) promises.push(refetchMeanLeft());
    }
    if (positionRight && dragItemRight) {
      promises.push(refetchRight());
      if (activeMean) promises.push(refetchMeanRight());
    }

    // Execute all refetches
    Promise.allSettled(promises);
  }, [
    loadData,
    installationsId.length,
    positionLeft,
    positionRight,
    dragItemLeft,
    dragItemRight,
    activeMean,
    refetchLeft,
    refetchRight,
    refetchMeanLeft,
    refetchMeanRight,
  ]);

  // Reset loadData when all fetches are complete
  useEffect(() => {
    if (!loadData) return;

    const leftDone = !positionLeft || !dragItemLeft || (!isLeftLoading && !isLeftFetching);
    const rightDone = !positionRight || !dragItemRight || (!isRightLoading && !isRightFetching);
    const leftMeanDone =
      !activeMean || !positionLeft || !dragItemLeft || (!isLeftMeanLoading && !isLeftMeanFetching);
    const rightMeanDone =
      !activeMean ||
      !positionRight ||
      !dragItemRight ||
      (!isRightMeanLoading && !isRightMeanFetching);
    const rainDone = !activeRain || (!isRainLoading && !isRainFetching);
    const healthDone = !activeHealthData || (!isHealthDataLoading && !isHealthDataFetching);
    const irrigationDone =
      !activeIrrigationData || (!isIrrigationDataLoading && !isIrrigationDataFetching);

    if (
      leftDone &&
      rightDone &&
      leftMeanDone &&
      rightMeanDone &&
      rainDone &&
      healthDone &&
      irrigationDone
    ) {
      setLoadData(false);
    }
  }, [
    loadData,
    isLeftLoading,
    isLeftFetching,
    isRightLoading,
    isRightFetching,
    isLeftMeanLoading,
    isLeftMeanFetching,
    isRightMeanLoading,
    isRightMeanFetching,
    isRainLoading,
    isRainFetching,
    isHealthDataLoading,
    isHealthDataFetching,
    isIrrigationDataLoading,
    isIrrigationDataFetching,
    positionLeft,
    positionRight,
    dragItemLeft,
    dragItemRight,
    activeMean,
    activeRain,
    activeHealthData,
    activeIrrigationData,
    setLoadData,
  ]);

  const hasAreas = areasId?.length > 0;
  const hasTrees = treesId?.length > 0;
  const hasInstallations = installationsId?.length > 0;
  const hasAnyVar = Boolean(dragItemLeft || dragItemRight);

  checkInitialEmpty = !(hasAreas && hasTrees && hasInstallations && hasAnyVar);

  anyFetchSuccessfull =
    isRightSuccess || isLeftSuccess || isSuccessRain || isSuccessHealth || isSuccessIrrigation
      ? true
      : false;
  checkLoading =
    isRightLoading ||
    isLeftLoading ||
    isLeftFetching ||
    isRightFetching ||
    isLeftMeanLoading ||
    isLeftMeanFetching ||
    isRightMeanLoading ||
    isRightMeanFetching ||
    isHealthDataLoading ||
    isHealthDataFetching ||
    isIrrigationDataLoading ||
    isIrrigationDataFetching ||
    isRainLoading ||
    isRainFetching
      ? true
      : false;

  const hasError = isLeftError || isRightError || isLeftMeanError || isRightMeanError;
  const hasPendingChanges = shouldRefetch && (!loadData || !checkLoading);

  const hasSeriesData = (series) =>
    Array.isArray(series) &&
    series.some((entry) => Array.isArray(entry?.data) && entry.data.length > 0);

  const hasPlottableData =
    hasSeriesData(leftResults) ||
    hasSeriesData(rightResults) ||
    hasSeriesData(leftMeanResults) ||
    hasSeriesData(rightMeanResults) ||
    hasSeriesData(rainData) ||
    hasSeriesData(irrigationData);

  const showNoDataState =
    !checkInitialEmpty &&
    !hasError &&
    !hasPendingChanges &&
    hasLoadedDataOnce &&
    !checkLoading &&
    anyFetchSuccessfull &&
    !hasPlottableData;

  useEffect(() => {
    if (hasError) {
      console.warn("DataScience fetch error:", {
        leftError,
        rightError,
        leftMeanError,
        rightMeanError,
      });
    }
  }, [hasError, leftError, rightError, leftMeanError, rightMeanError]);

  useEffect(() => {
    if (shouldRefetch && loadData && (checkLoading || anyFetchSuccessfull || hasError)) {
      setShouldRefetch(false);
    }
  }, [shouldRefetch, loadData, checkLoading, anyFetchSuccessfull, hasError, setShouldRefetch]);

  return (
    <div className="position-relative ds graph" style={{ height: "100%", overflow: "hidden" }}>
      {checkInitialEmpty ? (
        <EmptyArea tid={tid} dragItem={dragItemLeft} id={id} installationsId={installationsId} />
      ) : hasError ? (
        <div className="ds-error-state">
          <p className="ds-error-state__text">{t("ds_fetch_error_message")}</p>
          <p className="ds-error-state__hint">{t("ds_fetch_error_hint")}</p>
        </div>
      ) : hasPendingChanges ? (
        <div className="ds-pending-state">
          <p className="ds-pending-state__text">{t("ds_pending_reload_message")}</p>
          <p className="ds-pending-state__hint">{t("ds_pending_reload_hint")}</p>
        </div>
      ) : showNoDataState ? (
        <div className="ds-empty-state ds-empty-state--graph">
          <div>
            <p className="ds-empty-state__text">{t("ds_no_data_message")}</p>
            <p className="ds-empty-state__hint">{t("ds_no_data_hint")}</p>
          </div>
        </div>
      ) : (
        <>
          {checkLoading && (
            <div className="ds-graph-loading-overlay">
              <Spinner height="100%" />
            </div>
          )}
          <GraphArea
            checkLoading={checkLoading}
            rainData={rainData}
            irrigationData={irrigationData}
            anyFetchSuccessfull={anyFetchSuccessfull}
          />
        </>
      )}
    </div>
  );
}

export default GraphContainer;

function _getTsData({
  pid,
  installations,
  dragItem,
  position,
  startDate,
  endDate,
  lineColor,
  setYAxis,
  customInterval,
  mean,
  resetKey,
  moistureContentDisplayMode,
  moistureContentCapEnabled,
}) {
  const [allResults, setAllResults] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const lastAppliedResultKeyRef = useRef("");
  const partialResultsRef = useRef([]);
  const fetchRunIdRef = useRef(0);
  const compatibleInstallations = useMemo(
    () => getCompatibleInstallations(installations, dragItem),
    [installations, dragItem]
  );
  const installationIdsKey = compatibleInstallations.map((inst) => inst.id).join(",");

  // Clear results when key parameters change
  useEffect(() => {
    fetchRunIdRef.current += 1;
    partialResultsRef.current = [];
    setAllResults([]);
    setYAxis([]);
    setShouldFetch(false);
    lastAppliedResultKeyRef.current = "";
  }, [startDate, endDate, dragItem, installationIdsKey, customInterval, resetKey]);

  const canFetch = Boolean(
    compatibleInstallations.length > 0 &&
      dragItem &&
      position &&
      startDate &&
      endDate &&
      [
        "resistance",
        "temperature",
        "soil_moisture",
        "soil_water_tension",
        "soil_water_tension_vg",
        "soil_moisture_vg",
        "resis_temp_comp",
        "moisture_content",
        "diameter",
        "tree_water_deficit",
        "diameter_minus_start",
        "roll",
        "pitch",
      ].includes(dragItem) &&
      (mean === false || mean === true)
  );

  const installationsStr = installationIdsKey;
  const interval = mean ? "1d" : getInterval(startDate, endDate, customInterval);

  const processChunkData = useCallback(
    (rawData, installationSubset) => {
      const result = [];
      installationSubset.forEach((installation) => {
        const seriesName = `${installation.tree.name} ${installation.sensor.name}`;
        const installationData = rawData[installation.id];

        if (!Array.isArray(installationData)) return;

        const isMoisture = MOISTURE_CONTENT_FIELDS.includes(dragItem);
        const newData = installationData.map((value) => {
          const rawValue = value[dragItem];
          const displayValue = isMoisture
            ? scaleMoistureContentForDisplay(
                rawValue,
                moistureContentDisplayMode,
                moistureContentCapEnabled
              )
            : rawValue;

          return {
            type: dragItem,
            value: [value.time, displayValue],
            color: lineColor[installation.id],
            installation: installation,
            interval: interval,
            formulaType: value.formula_type || value.formulaType,
            calibrationSpecies: value.calibration_species || value.calibrationSpecies,
            ...(isMoisture &&
              value.blue_threshold != null && {
                blueThreshold: scaleMoistureThresholdForDisplay(
                  value.blue_threshold,
                  moistureContentDisplayMode,
                  moistureContentCapEnabled
                ),
              }),
            ...(isMoisture &&
              value.green_threshold != null && {
                greenThreshold: scaleMoistureThresholdForDisplay(
                  value.green_threshold,
                  moistureContentDisplayMode,
                  moistureContentCapEnabled
                ),
              }),
            ...(isMoisture &&
              value.yellow_threshold != null && {
                yellowThreshold: scaleMoistureThresholdForDisplay(
                  value.yellow_threshold,
                  moistureContentDisplayMode,
                  moistureContentCapEnabled
                ),
              }),
          };
        });

        let label = dragItem;
        let color = lineColor[installation.id];
        if (mean) {
          label += "-mean";
          color = (color || "#000") + "50";
        }

        result.push({
          name: seriesName,
          type: "line",
          label: label,
          installation: installation,
          color: color,
          data: newData,
        });
      });
      return result;
    },
    [dragItem, lineColor, mean, interval, moistureContentDisplayMode, moistureContentCapEnabled]
  );

  const queryKey = [
    "tsData",
    pid || "no-project",
    dragItem,
    installationsStr,
    startDate,
    endDate,
    Boolean(mean),
    customInterval,
  ];

  const { data, isSuccess, refetch, isLoading, isFetching, isError, error } = useQuery(
    queryKey,
    async () => {
      if (compatibleInstallations.length <= CHUNK_THRESHOLD) {
        return fetchTSDataByDate({
          installations: installationsStr,
          startDate,
          endDate,
          field: dragItem,
          interval: interval,
        });
      }

      const currentRunId = fetchRunIdRef.current;
      const chunks = chunkArray(compatibleInstallations, CHUNK_SIZE);
      partialResultsRef.current = [];
      const mergedData = {};

      for (const chunk of chunks) {
        if (currentRunId !== fetchRunIdRef.current) return mergedData;

        const chunkIds = chunk.map((inst) => inst.id).join(",");
        const chunkData = await fetchTSDataByDate({
          installations: chunkIds,
          startDate,
          endDate,
          field: dragItem,
          interval: interval,
        });
        Object.assign(mergedData, chunkData);

        if (currentRunId !== fetchRunIdRef.current) return mergedData;

        const chunkResults = processChunkData(chunkData, chunk);
        partialResultsRef.current = [...partialResultsRef.current, ...chunkResults];
        setYAxis([...partialResultsRef.current]);
      }

      return mergedData;
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  useEffect(() => {
    if (!shouldFetch || !isSuccess || !data || !dragItem) return;

    const result = processChunkData(data, compatibleInstallations);

    const resultKey = result
      .map((entry) => {
        const points = entry?.data || [];
        const first = points[0]?.value?.[0] ?? "";
        const last = points[points.length - 1]?.value?.[0] ?? "";
        return `${entry?.name || ""}|${entry?.label || ""}|${entry?.color || ""}|${
          points.length
        }|${first}|${last}`;
      })
      .join(";");
    if (lastAppliedResultKeyRef.current === resultKey) return;
    lastAppliedResultKeyRef.current = resultKey;

    setAllResults(result);
    setYAxis(result);
  }, [
    shouldFetch,
    isSuccess,
    data,
    dragItem,
    installationIdsKey,
    mean,
    startDate,
    endDate,
    customInterval,
    processChunkData,
  ]);

  const manualRefetch = useCallback(() => {
    if (canFetch) {
      fetchRunIdRef.current += 1;
      partialResultsRef.current = [];
      setShouldFetch(true);
      return refetch();
    }
    return Promise.resolve();
  }, [canFetch, refetch]);

  // Reset shouldFetch after successful fetch
  useEffect(() => {
    if (isSuccess) {
      setShouldFetch(false);
    }
  }, [isSuccess]);

  return {
    allResults,
    refetch: manualRefetch,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
  };
}

// D.Sensor health
function _getHealthData({ pid, healthTreeId, setHealthData, startDate, endDate }) {
  return useQuery(
    ["healthState", healthTreeId, startDate, endDate, pid || "no-project"],
    () => fetchTreeHealthByDate({ id: healthTreeId, startDate, endDate }),
    {
      refetchOnWindowFocus: false,
      enabled: false, // Disable automatic fetching - only fetch on manual refetch
      onSuccess: (data) => {
        const result = [];
        const b = data?.time?.map((value, i) => {
          let color = "";
          if (data.states[i] == 0) {
            color = GREEN;
          }
          if (data.states[i] == 1) {
            color = YELLOW;
          }
          if (data.states[i] == 2) {
            color = RED;
          }
          if (data.states[i] == 3) {
            color = GREY;
          }
          if (data.states[i] == 4) {
            color = BLUE;
          }
          return [
            {
              value: [value, 1],
              type: "health",
              state: data.states[i],
              color: color,
              itemStyle: {
                color: color,
              },
            },
          ];
        });

        result.push({
          name: "health",
          type: "bar",
          yAxisIndex: 2,
          barCategoryGap: -50,
          large: true,
          barWidth: 50,
          data: b ? b.flat() : [],
        });
        setHealthData(result);
      },
    }
  );
}

function _getIrrigationData({
  pid,
  irrigationTreeId,
  setIrrigationData,
  startDate,
  endDate,
  activeIrrigationData,
}) {
  return useQuery(
    ["irrigationTasks", irrigationTreeId, startDate, endDate, pid || "no-project"],
    () => getAllTasks({ treeId: irrigationTreeId }),
    {
      refetchOnWindowFocus: false,
      enabled: false, // Manual refetch only - consistent with explicit Load Data flow
      onSuccess: (data) => {
        const list = Array.isArray(data) ? data : data?.results || data?.data || [];
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate).getTime() : null;
        const toNumber = (value) => {
          if (value == null || value === "") return null;
          const normalized = String(value).replace(",", ".").trim();
          const parsed = Number(normalized);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const isIrrigationTask = (task) => {
          const taskTypes = [
            ...(Array.isArray(task?.tasks) ? task.tasks : []),
            ...(Array.isArray(task?.taskTypes) ? task.taskTypes : []),
          ];
          const text = [task?.title, task?.description, ...(taskTypes || [])]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return (
            taskTypes.includes(1) ||
            taskTypes.includes("1") ||
            taskTypes.includes("Irrigation") ||
            text.includes("irrigation") ||
            text.includes("bewasser") ||
            text.includes("bewässer") ||
            text.includes("giess") ||
            text.includes("gieß")
          );
        };

        const items = list
          .filter(
            (task) =>
              String(task?.treeId ?? task?.tree?.id ?? task?.tree_id ?? "") ===
              String(irrigationTreeId)
          )
          .filter(isIrrigationTask)
          .map((task) => {
            const time =
              task?.scheduledAt || task?.scheduled_at || task?.createdAt || task?.created_at;
            const ts = time ? new Date(time).getTime() : null;
            const liters =
              toNumber(task?.data?.irrigationLog?.waterLiters) ??
              toNumber(task?.data?.mengeLiter) ??
              toNumber(task?.data?.waterLiters) ??
              toNumber(task?.mengeLiter) ??
              toNumber(task?.waterLiters);
            return { task, time, ts, liters };
          })
          .filter(({ ts }) => Number.isFinite(ts))
          .filter(({ liters }) => Number.isFinite(liters))
          .filter(({ ts }) => {
            if (start == null || end == null) return true;
            return ts >= start && ts <= end;
          });

        const result = [
          {
            name: "irrigation",
            type: "bar",
            yAxisIndex: 4,
            barWidth: 8,
            barCategoryGap: "30%",
            itemStyle: { color: "var(--bs-primary, #0d6efd)" },
            data: items.map(({ task, time, liters }) => ({
              value: [time, liters],
              type: "irrigation",
              color: "var(--bs-primary, #0d6efd)",
              title: task?.title || "Irrigation",
              liters,
            })),
          },
        ];

        setIrrigationData(result);
      },
    }
  );
}

// E.Rain data
function _getRain({ pid, id, activeRain, setRainData, startDate, endDate }) {
  return useQuery(
    ["rain", id, startDate, endDate, pid || "no-project"],
    () => fetchRainDataByDate({ id, startDate, endDate }),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data) => {
        const { rain } = data;
        const res = [];
        const b = rain?.x?.map((value, i) => {
          return [
            {
              value: [value, rain?.y[i]],
              color: "var(--bs-primary, #0d6efd)",
            },
          ];
        });
        res.push({
          name: "rain",
          type: "line",
          smooth: true,
          showSymbol: false,
          yAxisIndex: 3,
          barCategoryGap: -50,
          color: "var(--bs-primary, #0d6efd)",
          areaStyle: { color: "var(--bs-primary, #0d6efd)" },
          data: b ? b.flat() : [],
        });
        activeRain && setRainData(res);
      },
    }
  );
}
